import { useState } from 'react';
import { api, saveFile, type OfficeClient } from '../api';
import type { Dictionary } from '../i18n';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';

/**
 * Masowa wysyłka JPK z panelu biura (MVP, panel biura): pliki wszystkich klientów naraz, jedna
 * paczka metadanych do podpisu podpisem biura i wgranie podpisanych plików hurtem. Plik z paczki
 * zaczyna się od identyfikatora wysyłki, więc po podpisie wiadomo, do której firmy należy.
 */
export function OfficeJpk({ officeId, month, clients, t }: { officeId: string; month: string; clients: OfficeClient[]; t: Dictionary }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [sent, setSent] = useState<number | null>(null);
  const accepted = clients.filter((client) => client.status === 'accepted');

  async function act(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  async function upload(files: FileList | null) {
    if (!files) return;
    await act(async () => {
      const submissions = await api.officeJpkSubmissions(officeId, month);
      let count = 0;
      for (const file of Array.from(files)) {
        const submission = submissions.find((item) => file.name.startsWith(item.id));
        if (!submission) continue;
        await api.jpkSigned(submission.companyId, submission.id, await file.text());
        count += 1;
      }
      setSent(count);
    });
  }

  const name = (companyId: string) => clients.find((client) => client.companyId === companyId)?.companyName ?? companyId;

  return (
    <div className="panel">
      <header>
        <h4>{t.officeJpk.title}</h4>
      </header>
      <p className="sm muted">{t.officeJpk.lead}</p>
      <ErrorAlert error={error} t={t} />
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn"
          disabled={busy || accepted.length === 0}
          onClick={() =>
            void act(async () => {
              const result = await api.officeJpkPrepare(officeId, month, accepted.map((client) => client.companyId));
              setProblems(result.problems);
            })
          }
        >
          {t.officeJpk.prepare}
        </button>
        <button type="button" className="btn sec" disabled={busy} onClick={() => void act(async () => saveFile(await api.officeJpkToSign(officeId, month)))}>
          <Icon.Download />
          {t.officeJpk.download}
        </button>
        <label className="btn sec">
          <Icon.Upload />
          {t.officeJpk.upload}
          <input type="file" multiple accept=".xml,.xades" hidden onChange={(event) => void upload(event.target.files)} />
        </label>
      </div>
      {sent !== null && <p className="sm">{t.officeJpk.sent(sent)}</p>}
      {Object.keys(problems).length > 0 && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.officeJpk.problems}</b>
            {Object.entries(problems).map(([companyId, problem]) => (
              <p key={companyId} className="sm">
                {name(companyId)}: {problem}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
